import { BaseRenderer } from '@/modules/core/ui/renderers/BaseRenderer';
import ClaraStreamService from '@/modules/ai/services/ClaraStreamService';
import ClaraService from '@/modules/ai/services/ClaraService';
import { ClaraConversationObject } from '@/modules/ai/models/ClaraConversationObject';
import { AskClaraQuestionResponseObject } from '@/modules/ai/models/AskClaraQuestionResponseObject';
import { ClaraConversationOrigin } from '@/modules/ai/constants/ClaraConversationOrigin';
import { ClaraEventPayloadType } from '@/modules/ai/constants/ClaraEventPayloadType';

export interface ClaraStreamingPresenterCallbacks {
  onStreamingStarted(): void;
  onStreamedTextUpdated(text: string): void;
  onFetchingFinalStarted(): void;
  onFinalAnswerReceived(response: AskClaraQuestionResponseObject): void;
  onFallbackStarted(): void;
  onFallbackAnswerReceived(response: AskClaraQuestionResponseObject): void;
  onFailed(): void;
}

export class ClaraStreamingPresenter extends BaseRenderer {
  private static readonly FLUSH_INTERVAL_MS: number = 100;

  private conversation: ClaraConversationObject;
  private ceUserId: number;
  private origin: ClaraConversationOrigin;
  private callbacks: ClaraStreamingPresenterCallbacks;
  private streamBuffer: string = '';
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private eventSource: EventSource | null = null;
  private preBuffer: string = '';
  private inAnswer: boolean = false;
  private insideCitation: boolean = false;
  private citationBuffer: string = '';
  private escapeBuffer: string = '';

  constructor(
    conversation: ClaraConversationObject,
    ceUserId: number,
    origin: ClaraConversationOrigin,
    callbacks: ClaraStreamingPresenterCallbacks
  ) {
    super();
    this.conversation = conversation;
    this.ceUserId = ceUserId;
    this.origin = origin;
    this.callbacks = callbacks;
  }

  async askQuestion(): Promise<void> {
    this.callbacks.onStreamingStarted();
    this.resetBuffer();
    this.resetParserState();
    this.closeEventSource();

    try {
      const source: EventSource = await ClaraStreamService.openStream({
        text: this.conversation.latestQuestion.text ?? '',
        identifier: this.conversation.latestQuestion.identifier ?? '',
        modelName: this.conversation.modelName ?? '',
        conversationId: this.conversation.id ?? 0,
        ceUserId: this.ceUserId,
        origin: this.origin
      });

      this.bindEventSource(source);
    } catch {
      this.fallback();
    }
  }

  destroy(): void {
    this.clearFlushTimer();
    this.closeEventSource();
  }

  private bindEventSource(source: EventSource): void {
    this.eventSource = source;

    source.onmessage = (event: MessageEvent): void => {
      try {
        const payload: Record<string, string> = JSON.parse(event.data);

        switch (payload.type) {
          case ClaraEventPayloadType.TOKEN:
            this.onToken(payload.content);
            break;
          case ClaraEventPayloadType.DONE:
            this.closeEventSource();
            this.flushBuffer();
            this.fetchFinalAnswer();
            break;
          case ClaraEventPayloadType.CLARIFICATION:
            this.closeEventSource();
            this.clearFlushTimer();
            this.fallback();
            break;
          case ClaraEventPayloadType.ERROR:
            this.closeEventSource();
            this.clearFlushTimer();
            this.fallback();
            break;
        }
      } catch {
        // Ignore parse errors
      }
    };

    source.onerror = (): void => {
      this.closeEventSource();
      this.clearFlushTimer();
      this.fallback();
    };
  }

  private closeEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private onToken(rawToken: string): void {
    const cleaned: string | null = this.processToken(rawToken);
    if (!cleaned) return;

    this.streamBuffer += cleaned;

    if (!this.flushTimer) {
      this.flushTimer = setTimeout((): void => {
        this.callbacks.onStreamedTextUpdated(this.streamBuffer);
        this.flushTimer = null;
      }, ClaraStreamingPresenter.FLUSH_INTERVAL_MS);
    }
  }

  private flushBuffer(): void {
    this.clearFlushTimer();
    this.callbacks.onStreamedTextUpdated(this.streamBuffer);
  }

  private async fetchFinalAnswer(): Promise<void> {
    this.callbacks.onFetchingFinalStarted();

    try {
      const response: AskClaraQuestionResponseObject = await ClaraService.askQuestion(
        this.conversation.latestQuestion.text ?? '',
        this.conversation.latestQuestion.identifier ?? '',
        this.conversation.modelName ?? '',
        this.conversation.id ?? 0,
        this.origin
      );

      this.callbacks.onFinalAnswerReceived(response);
    } catch {
      this.callbacks.onFailed();
    }
  }

  private async fallback(): Promise<void> {
    this.callbacks.onFallbackStarted();
    this.resetBuffer();

    try {
      const response: AskClaraQuestionResponseObject = await ClaraService.askQuestion(
        this.conversation.latestQuestion.text ?? '',
        this.conversation.latestQuestion.identifier ?? '',
        this.conversation.modelName ?? '',
        this.conversation.id ?? 0,
        this.origin
      );

      this.callbacks.onFallbackAnswerReceived(response);
    } catch {
      this.callbacks.onFailed();
    }
  }

  private processToken(token: string): string | null {
    if (!this.inAnswer) {
      this.preBuffer += token;
      const match: RegExpMatchArray | null = this.preBuffer.match(/"answer"\s*:\s*"/);
      if (match) {
        this.inAnswer = true;
        const markerEnd: number = this.preBuffer.indexOf(match[0]) + match[0].length;
        const afterMarker: string = this.preBuffer.substring(markerEnd);
        this.preBuffer = '';
        if (!afterMarker) return null;
        return this.cleanContent(afterMarker);
      }
      return null;
    }

    return this.cleanContent(token);
  }

  private cleanContent(text: string): string | null {
    const endIdx: number = this.findUnescapedQuote(text);
    if (endIdx !== -1) {
      this.inAnswer = false;
      text = text.substring(0, endIdx);
    }

    this.citationBuffer += text;

    if (this.insideCitation) {
      const closeIdx: number = this.citationBuffer.indexOf('</citation>');
      if (closeIdx !== -1) {
        this.citationBuffer = this.citationBuffer.substring(closeIdx + '</citation>'.length);
        this.insideCitation = false;
        const remaining: string = this.citationBuffer;
        this.citationBuffer = '';
        if (!remaining) return null;
        return this.cleanContent(remaining);
      }
      return null;
    }

    const openIdx: number = this.citationBuffer.indexOf('<citation>');
    if (openIdx !== -1) {
      const before: string = this.citationBuffer.substring(0, openIdx);
      this.citationBuffer = this.citationBuffer.substring(openIdx);
      this.insideCitation = true;

      const closeIdx: number = this.citationBuffer.indexOf('</citation>');
      if (closeIdx !== -1) {
        this.citationBuffer = this.citationBuffer.substring(closeIdx + '</citation>'.length);
        this.insideCitation = false;
      }

      if (!before) return null;
      return this.unescape(before);
    }

    const lastLt: number = this.citationBuffer.lastIndexOf('<');
    if (lastLt !== -1) {
      const tail: string = this.citationBuffer.substring(lastLt);
      if ('<citation>'.startsWith(tail)) {
        const emit: string = this.citationBuffer.substring(0, lastLt);
        this.citationBuffer = tail;
        if (!emit) return null;
        return this.unescape(emit);
      }
    }

    const emit: string = this.citationBuffer;
    this.citationBuffer = '';
    if (!emit) return null;
    return this.unescape(emit);
  }

  private unescape(text: string): string {
    text = this.escapeBuffer + text;
    this.escapeBuffer = '';

    if (text.endsWith('\\')) {
      this.escapeBuffer = '\\';
      text = text.slice(0, -1);
    }

    return text
      .replace(/\\u([0-9a-fA-F]{4})/g, (_: string, hex: string) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  }

  private findUnescapedQuote(text: string): number {
    for (let i: number = 0; i < text.length; i++) {
      if (text[i] === '"' && (i === 0 || text[i - 1] !== '\\')) {
        const after: string = text.substring(i + 1).trimStart();
        if (after.startsWith(',') || after.startsWith('}') || after === '') {
          return i;
        }
      }
    }
    return -1;
  }

  private resetBuffer(): void {
    this.streamBuffer = '';
    this.clearFlushTimer();
  }

  private resetParserState(): void {
    this.preBuffer = '';
    this.inAnswer = false;
    this.insideCitation = false;
    this.citationBuffer = '';
    this.escapeBuffer = '';
  }

  private clearFlushTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}
